import { useEffect, useState, useCallback } from 'react';
import { listsApi, ApiError } from '../api/client';
import type { ListRow } from '../api/types';

export function useLists() {
  const [lists, setLists] = useState<ListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listsApi.list();
      setLists(data);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 监听同步事件 → 自动 refresh
  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent).detail as { entity?: string };
      // 任意变更都刷新 (跨设备)
      if (!detail?.entity || detail.entity === 'list') refresh();
    };
    const onSnapshot = () => refresh();
    window.addEventListener('todo:change', onChange);
    window.addEventListener('todo:snapshot', onSnapshot);
    return () => {
      window.removeEventListener('todo:change', onChange);
      window.removeEventListener('todo:snapshot', onSnapshot);
    };
  }, [refresh]);

  const create = useCallback(
    async (name: string) => {
      const row = await listsApi.create({ name });
      await refresh();
      return row;
    },
    [refresh]
  );

  const update = useCallback(
    async (id: string, patch: { name?: string; color?: string; icon?: string }) => {
      const row = await listsApi.update(id, patch);
      await refresh();
      return row;
    },
    [refresh]
  );

  const remove = useCallback(
    async (id: string) => {
      await listsApi.remove(id);
      await refresh();
    },
    [refresh]
  );

  return { lists, loading, error, refresh, create, update, remove };
}